/**
 * Formats a number with thousand separators
 * @param {number|string} value - The number to format
 * @param {Object} options - Formatting options
 * @param {string} options.locale - Locale for formatting (default: 'en-US')
 * @param {number} options.minimumFractionDigits - Minimum fraction digits (default: 0)
 * @param {number} options.maximumFractionDigits - Maximum fraction digits (default: 0)
 * @returns {string} Formatted number string
 */
export const formatNumber = (value, options = {}) => {
  if (value === null || value === undefined || value === '') {
    return '-';
  }

  const num = typeof value === 'string' ? parseFloat(value) : value;

  if (isNaN(num)) {
    return '-';
  }

  const defaultOptions = {
    locale: 'en-US',
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
    ...options,
  };

  return num.toLocaleString(defaultOptions.locale, {
    minimumFractionDigits: defaultOptions.minimumFractionDigits,
    maximumFractionDigits: defaultOptions.maximumFractionDigits,
  });
};

/**
 * Formats a percentage value
 * @param {number} value - The percentage value (0-100)
 * @param {number} decimals - Number of decimal places (default: 1)
 * @returns {string} Formatted percentage string
 */
export const formatPercentage = (value, decimals = 1) => {
  if (value === null || value === undefined || value === '') {
    return '-';
  }

  const num = typeof value === 'string' ? parseFloat(value) : value;

  if (isNaN(num)) {
    return '-';
  }

  return `${num.toFixed(decimals)}%`;
};

/**
 * Formats large numbers with K, M, B suffixes
 * @param {number} value - The number to format
 * @param {number} decimals - Number of decimal places (default: 1)
 * @returns {string} Formatted number with suffix
 */
export const formatCompactNumber = (value, decimals = 1) => {
  if (value === null || value === undefined || value === '') {
    return '-';
  }

  const num = typeof value === 'string' ? parseFloat(value) : value;

  if (isNaN(num)) {
    return '-';
  }

  const absNum = Math.abs(num);
  const sign = num < 0 ? '-' : '';

  if (absNum >= 1e9) {
    return `${sign}${(absNum / 1e9).toFixed(decimals)}B`;
  } else if (absNum >= 1e6) {
    return `${sign}${(absNum / 1e6).toFixed(decimals)}M`;
  } else if (absNum >= 1e3) {
    return `${sign}${(absNum / 1e3).toFixed(decimals)}K`;
  }

  return formatNumber(num);
};
